import { Injectable, Injector } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { CairaAuthService } from './caira-auth.service';

@Injectable()
export class CairaAuthInterceptor implements HttpInterceptor {
  // Resolve Router/CairaAuthService lazily (via Injector) to avoid any cyclic
  // dependency at bootstrap that injecting Router into an interceptor can cause.
  constructor(private injector: Injector) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Retrieve the Caira-specific token. Must match the key the QR login writes
    // (CairaAuthService.ACCESS_TOKEN = "caira_access_token").
    const token = localStorage.getItem('caira_access_token');
    const isCairaApi = req.url.includes('api.milescaira.com');

    /**
     * Only attach the token if:
     * 1. A token exists in localStorage
     * 2. The request URL belongs to the Caira API domain
     */
    const handled$ =
      token && isCairaApi
        ? next.handle(
            req.clone({
              setHeaders: {
                Authorization: `Bearer ${token}`,
                skip: 'true',
              },
            })
          )
        : next.handle(req);

    // Only watch CAIRA API responses for auth expiry.
    if (!isCairaApi) return handled$;

    return handled$.pipe(
      catchError((err: HttpErrorResponse) => {
        if (this.isAuthExpiry(err)) {
          this.handleSessionExpiry();
        }
    // Value form, NOT `throwError(() => error)`. This project is on RxJS 6,
    // where throwError takes the error ITSELF — the factory overload arrived
    // in RxJS 7. Passing an arrow function emits the FUNCTION as the error, so
    // every downstream handler sees a function instead of an HttpErrorResponse
    // and falls through to its generic fallback. Measured: the register error
    // copy on CPA rendered "Registration failed. Please try again." for every
    // failure, including the unenrolled case that has its own wording.
        return throwError(err);
      })
    );
  }

  /** A CAIRA 401 (token invalid/expired), or a 403 that clearly says the
   *  token/signature has expired, means the session is no longer valid. */
  private isAuthExpiry(err: HttpErrorResponse): boolean {
    if (!(err instanceof HttpErrorResponse)) return false;
    if (err.status === 401) return true;
    if (err.status === 403) {
      // Redirect on token/session failures, but NOT on genuine permission 403s
      // (e.g. "You do not have permission to perform this action.").
      const detail = JSON.stringify(err.error ?? '').toLowerCase();
      return /expired|signature|invalid token|credentials|authentication|unauthor/.test(
        detail
      );
    }
    return false;
  }

  /** Clear the dead CAIRA session and send the user back to the QR login,
   *  instead of leaving a blank screen when API calls start failing. */
  private handleSessionExpiry(): void {
    const router = this.injector.get(Router);
    const cairaAuth = this.injector.get(CairaAuthService);
    const url = router.url || '';
    // Don't loop on the login-style screens (login / account-update / maintenance).
    if (/\/caira-ready\/(login|account-update|maintenance)/.test(url)) return;
    cairaAuth.logout();
    router.navigate(['/lms/caira-ready/login'], {
      queryParams: { sessionExpired: 1 },
    });
  }
}
